// Loads the active trip pack: the cached copy first (works offline, instant), then
// refreshes from the network in the background when there's signal.
import { create } from 'zustand'
import { composeElsewhere, composeTrip, NO_KIT } from '../../shared/pack/compose'
import { KitBundle } from '../../shared/pack/kits'
import { Pack } from '../../shared/pack/schema'
import { api } from './api'
import { kvGet, kvSet } from './db'

interface PackState {
  pack: Pack | null
  /** The family's first trip (the pack index's default): events from before trips existed are its (D1). */
  firstTrip: string | null
  error: string | null
  /** Loads cache, then network. Resolves once something usable is in `pack` (or both failed). */
  load(): Promise<void>
}

const CACHE_KEY = 'pack'

interface ActiveTrip {
  id: string
  /** A trip made in the app (D2): composed here from its kit bundle (D3). */
  title?: string
  app?: { kit: string; startDate: string; endDate: string; timezone: string }
}

async function fetchActivePack(): Promise<Pack> {
  const active = await api<ActiveTrip>('/api/pack/active').catch(async () => ({ id: (await api<{ default: string }>('/packs/index.json')).default }) as ActiveTrip)
  return packFor(active)
}

/** Any trip's pack (a built pack, an in-app trip from its kit, or a "Somewhere else" trip), e.g. a past one (B10). */
export async function packFor(active: ActiveTrip): Promise<Pack> {
  if (active.app?.kit === NO_KIT) return composeElsewhere({ id: active.id, title: active.title ?? active.id, ...active.app })
  if (active.app) {
    const kit = KitBundle.parse(await api<unknown>(`/packs/kits/${active.app.kit}.json`))
    return composeTrip(kit, { id: active.id, title: active.title ?? active.id, ...active.app })
  }
  const raw = await api<unknown>(`/packs/${active.id}/pack.json`)
  return Pack.parse(raw)
}

export const usePack = create<PackState>((set, get) => ({
  pack: null,
  firstTrip: null,
  error: null,
  async load() {
    const cached = await kvGet<unknown>(CACHE_KEY)
    const parsed = cached ? Pack.safeParse(cached) : null
    if (parsed?.success) set({ pack: parsed.data })
    set({ firstTrip: (await kvGet<string>('firstTrip')) ?? null })
    void api<{ default: string }>('/packs/index.json')
      .then(async (index) => {
        await kvSet('firstTrip', index.default)
        set({ firstTrip: index.default })
      })
      .catch(() => undefined)

    const fresh = fetchActivePack().then(async (pack) => {
      const current = get().pack
      // The same version with other decks is the pack bought or refunded (C5: a family without it gets the free deck only).
      if (!current || current.meta.id !== pack.meta.id || current.meta.version !== pack.meta.version || current.decks.length !== pack.decks.length) {
        set({ pack, error: null })
      }
      await kvSet(CACHE_KEY, pack)
    })
    if (get().pack) {
      fresh.catch(() => undefined) // offline with a cached pack: fine
      return
    }
    await fresh.catch((e: unknown) => set({ error: e instanceof Error ? e.message : String(e) }))
  },
}))
